'use client';

import { c as _c } from "react/compiler-runtime";
import useSWR from 'swr';
import { useCallback } from 'react';
import { useSession } from 'next-auth/react';

// Actions for mutations (user-triggered, not on page load)
import updateCartItemAction from '@/app/_lib/actions/UpdateCartItemAction';
import deleteCartItemAction from '@/app/_lib/actions/DeleteCartItemAction';
import saveCartItemForLaterAction from '@/app/_lib/actions/SaveCartItemForLaterAction';
import moveSavedItemToCartAction from '@/app/_lib/actions/MoveSavedItemToCartAction';
import deleteSavedItemAction from '@/app/_lib/actions/DeleteSavedItemAction';
import { applyPromotionCode, removePromotionCode } from '@/app/_lib/actions/ValidatePromotionAction';

// Fetchers use Route Handlers (not Server Actions) to avoid bot/crawler errors.
// Bots that render JS dispatch Server Actions on mount, causing RSC errors.
// GET requests to Route Handlers don't have this problem.
const cartFetcher = async () => {
  const response = await fetch('/api/cart');
  const data = await response.json();
  if (data.success) {
    return data.cart;
  }
  throw new Error('Failed to fetch cart');
};
const savedItemsFetcher = async () => {
  const response = await fetch('/api/cart/saved-items');
  const data = await response.json();
  if (data.success) {
    return data.saved_items || [];
  }
  throw new Error('Failed to fetch saved items');
};

/**
 * useCart - Centralized cart state and operations hook
 *
 * Provides:
 * - Cart data with SWR caching
 * - Saved items data with SWR caching
 * - All cart mutations (delete, update, save for later, etc.)
 * - Automatic cache invalidation after mutations
 *
 * Usage:
 *   const { cart, savedItems, deleteItem, updateItem, ... } = useCart()
 *
 * Returns:
 *   cart: object - full cart data (items, totals, expressCheckoutData, etc.)
 *   quantity: number - total item count for badge display
 *   isLoading: boolean - true during initial cart fetch
 *   savedItems: array - items saved for later
 *   savedItemsLoading: boolean - true during saved items fetch
 *   refresh: function - manually refetch cart
 *   refreshSavedItems: function - manually refetch saved items
 *   refreshAll: function - refetch both cart and saved items
 *
 * Mutations (all return Promise<boolean> indicating success):
 *   deleteItem(itemIndex) - remove item from cart
 *   updateItem(itemIndex, cartItem) - update item (e.g., quantity change)
 *   saveForLater(itemIndex) - move cart item to saved items
 *   moveSavedToCart(itemIndex) - move saved item back to cart
 *   deleteSavedItem(itemIndex) - remove item from saved items
 */
export function useCart() {
  const $ = _c(49);
  const {
    status: sessionStatus
  } = useSession();
  let t0;
  if ($[0] !== sessionStatus) {
    t0 = sessionStatus === "loading" ? null : ["cart", sessionStatus];
    $[0] = sessionStatus;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      revalidateOnFocus: false,
      revalidateOnReconnect: false
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data: cart,
    isLoading: cartLoading,
    mutate: mutateCart
  } = useSWR(t0, cartFetcher, t1);
  let t2;
  if ($[3] !== sessionStatus) {
    t2 = sessionStatus === "loading" ? null : ["savedItems", sessionStatus];
    $[3] = sessionStatus;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      revalidateOnFocus: false,
      revalidateOnReconnect: false
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    data: savedItems,
    isLoading: savedItemsLoading,
    mutate: mutateSavedItems
  } = useSWR(t2, savedItemsFetcher, t3);
  let t4;
  if ($[6] !== mutateCart) {
    t4 = () => mutateCart();
    $[6] = mutateCart;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const refresh = t4;
  let t5;
  if ($[8] !== mutateSavedItems) {
    t5 = () => mutateSavedItems();
    $[8] = mutateSavedItems;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const refreshSavedItems = t5;
  let t6;
  if ($[10] !== mutateCart || $[11] !== mutateSavedItems) {
    t6 = async () => {
      await Promise.all([mutateCart(), mutateSavedItems()]);
    };
    $[10] = mutateCart;
    $[11] = mutateSavedItems;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const refreshAll = t6;
  let t7;
  if ($[13] !== mutateCart) {
    t7 = async itemIndex => {
      const response = await deleteCartItemAction(itemIndex);
      if (response.success) {
        await mutateCart();
        return true;
      }
      return false;
    };
    $[13] = mutateCart;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const deleteItem = t7;
  let t8;
  if ($[15] !== mutateCart) {
    t8 = async (itemIndex_0, cartItem) => {
      const response_0 = await updateCartItemAction(itemIndex_0, cartItem);
      if (response_0.success) {
        await mutateCart();
        return true;
      }
      return false;
    };
    $[15] = mutateCart;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const updateItem = t8;
  let t9;
  if ($[17] !== mutateCart || $[18] !== mutateSavedItems) {
    t9 = async itemIndex_1 => {
      const response_1 = await saveCartItemForLaterAction(itemIndex_1);
      if (response_1.success) {
        await Promise.all([mutateCart(), mutateSavedItems()]);
        return true;
      }
      return false;
    };
    $[17] = mutateCart;
    $[18] = mutateSavedItems;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const saveForLater = t9;
  let t10;
  if ($[20] !== mutateCart || $[21] !== mutateSavedItems) {
    t10 = async itemIndex_2 => {
      const response_2 = await moveSavedItemToCartAction(itemIndex_2);
      if (response_2.success) {
        await Promise.all([mutateCart(), mutateSavedItems()]);
        return true;
      }
      return false;
    };
    $[20] = mutateCart;
    $[21] = mutateSavedItems;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const moveSavedToCart = t10;
  let t11;
  if ($[23] !== mutateSavedItems) {
    t11 = async itemIndex_3 => {
      const response_3 = await deleteSavedItemAction(itemIndex_3);
      if (response_3.success) {
        await mutateSavedItems();
        return true;
      }
      return false;
    };
    $[23] = mutateSavedItems;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const deleteSavedItem = t11;
  let t12;
  if ($[25] !== mutateCart) {
    t12 = async code => {
      const response_4 = await applyPromotionCode(code);
      await mutateCart();
      return response_4;
    };
    $[25] = mutateCart;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const applyPromotion = t12;
  let t13;
  if ($[27] !== mutateCart) {
    t13 = async () => {
      const response_5 = await removePromotionCode();
      await mutateCart();
      return response_5.success;
    };
    $[27] = mutateCart;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  const clearPromotion = t13;
  const t14 = cart || null;
  const t15 = cart?.cartQuantity || 0;
  const t16 = cart?.promotion || null;
  const t17 = cartLoading || sessionStatus === "loading";
  let t18;
  if ($[29] !== savedItems) {
    t18 = savedItems || [];
    $[29] = savedItems;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  const t19 = savedItemsLoading || sessionStatus === "loading";
  const t20 = cart?.isTradeCustomer || false;
  let t21;
  if ($[31] !== applyPromotion || $[32] !== clearPromotion || $[33] !== deleteItem || $[34] !== deleteSavedItem || $[35] !== moveSavedToCart || $[36] !== refresh || $[37] !== refreshAll || $[38] !== refreshSavedItems || $[39] !== saveForLater || $[40] !== t14 || $[41] !== t15 || $[42] !== t16 || $[43] !== t17 || $[44] !== t18 || $[45] !== t19 || $[46] !== t20 || $[47] !== updateItem) {
    t21 = {
      cart: t14,
      quantity: t15,
      promotion: t16,
      isLoading: t17,
      savedItems: t18,
      savedItemsLoading: t19,
      refresh,
      refreshSavedItems,
      refreshAll,
      deleteItem,
      updateItem,
      saveForLater,
      moveSavedToCart,
      deleteSavedItem,
      applyPromotion,
      clearPromotion,
      isTradeCustomer: t20
    };
    $[31] = applyPromotion;
    $[32] = clearPromotion;
    $[33] = deleteItem;
    $[34] = deleteSavedItem;
    $[35] = moveSavedToCart;
    $[36] = refresh;
    $[37] = refreshAll;
    $[38] = refreshSavedItems;
    $[39] = saveForLater;
    $[40] = t14;
    $[41] = t15;
    $[42] = t16;
    $[43] = t17;
    $[44] = t18;
    $[45] = t19;
    $[46] = t20;
    $[47] = updateItem;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  return t21;
}